import posthog from 'posthog-js'
// Bundle the replay recorder with the app so short, first-time visits do not
// depend on a second runtime script download before the initial DOM snapshot.
import 'posthog-js/dist/posthog-recorder.js'
import {
  isIPhone,
} from '../utils/inAppBrowser'
import {
  normalizeReferralCode,
} from '../utils/referral'
import {
  hasCapabilityToken,
  sanitizeBeforeSend,
} from './urlSanitizer'
import {
  isOnboardingDemoPath,
} from '../utils/routes'

// Nobody on the onboarding demo is a real visitor, so on that page every helper
// here behaves as if analytics were not configured. That also keeps the
// feature-flag reads from waiting on a client that is never initialised.
const POSTHOG_KEY = typeof window !== 'undefined' && isOnboardingDemoPath(window.location.pathname)
  ? undefined
  // @ts-ignore - Vite env types
  : import.meta.env?.VITE_POSTHOG_KEY as string | undefined
// @ts-ignore - Vite env types
const POSTHOG_HOST = import.meta.env?.VITE_POSTHOG_HOST as string | undefined
// @ts-ignore - Vite env types
// Fallback mirrors the docker-compose default (`development-${USER:-unknown}`)
// so a build with `VITE_ENVIRONMENT` unset still namespaces correctly instead
// of silently colliding with another developer's `development-` person.
const ENVIRONMENT = (import.meta.env?.VITE_ENVIRONMENT as string | undefined) ?? 'development-unknown'

// Production environment label. Anything else (e.g. "development-itamar") is a
// non-prod env that shares the same PostHog project but must not collide with
// prod persons; we namespace distinct_ids with `{env}:` for those. Mirrors the
// backend convention in `app/services/feature_flags.py::_env_prefixed_distinct_id`.
const PROD_ENVIRONMENT = 'dev'

function envPrefixedDistinctId(rawId: string): string {
  return ENVIRONMENT === PROD_ENVIRONMENT ? rawId : `${ENVIRONMENT}:${rawId}`
}

// The inverse test: an identified id is a Catch user id (prefixed as above),
// while an anonymous one is the UUID posthog-js minted.
function isIdentifiedDistinctId(id: string | null): boolean {
  return id != null && /^(?:.+:)?\d+$/.test(id)
}

let isInitialized = false
let sessionEnding = false

// Mirrors the current PostHog *anonymous* distinct_id into a cookie readable by
// the backend. The backend uses it as $anon_distinct_id on server-side events
// that fire before frontend identify (e.g. the OAuth callback) so those events
// merge onto the same PostHog person as the browser session — instead of
// creating a separate person keyed by the numeric user_id.
//
// Only the anon ID is written here: after identify the cookie is intentionally
// left as-is (stale anon ID is still a valid merge hint and the backend skips
// emitting the hint when it equals the user_id anyway). On reset() the cookie
// must be rewritten with the freshly-minted anon ID (see resetUser).
//
// NOTE: The cookie name "ph_did" is duplicated as POSTHOG_DISTINCT_ID_COOKIE_NAME
// in backend/src/app/routers/auth.py — keep them in sync.
function syncDistinctIdCookie(distinctId: string): void {
  const hostname = window.location.hostname
  const isLocalhost = hostname === 'localhost' || hostname.startsWith('127.')
  const domainPart = isLocalhost ? '' : '; domain=.catchagent.ai'
  const secure = window.location.protocol === 'https:' ? '; Secure' : ''
  document.cookie = `ph_did=${encodeURIComponent(distinctId)}; path=/${domainPart}; max-age=31536000; SameSite=Lax${secure}`
}

// Feature flag constants
export const FeatureFlags = {
  SMS_CHANNEL: 'sms-channel',
  // Shared Twilio guest pool (GET-18126): shows the Settings box consenting to
  // the assistant texting the owner's contacts. The backend flag of the same
  // name gates the sends.
  GUEST_SMS_POOL: 'guest-sms-pool',
  CUSTOM_INTEGRATION_BUILDER: 'custom-integration-builder',
  // Presentational ONLY: whether the "Connect any app" browse catalog renders
  // on Settings -> Integrations. Internal-cohort while the catalog's ~thousands
  // of apps stay too broad an answer for a new user, whose useful shortlist is
  // the inbox scan's suggestions.
  //
  // This is NOT the split `list_live_suggestions` warns against, and the
  // direction is the whole distinction. That warning is about serving Pipedream
  // *tiles* while `generic-pipedream-integrations` stays off — which leaves the
  // agent gates (`run_pipedream_app_query`, `tool_filter`, the per-run access
  // flag) shut, so a user connects an app and the assistant denies knowing it.
  // This flag never serves a tile: it only ever *hides a browse surface* for
  // users whose capability flag is already on, so everything connectable stays
  // fully usable. It must therefore never gate a connect path or a suggestion
  // row — only the catalog card and the affordances that open it.
  PIPEDREAM_CATALOG_BROWSE: 'pipedream-catalog-browse',
  // Connect a personal (secondary) inbox alongside the work account. Mirrors
  // the backend's `connected-personal-email` flag, which gates the connect
  // endpoints and the sync/sweep of connected inboxes.
  CONNECTED_PERSONAL_EMAIL: 'connected-personal-email',
  // Buying prepaid purchase credits from Settings. Mirrors the backend's
  // `purchase-credits-topup` flag, which gates the two endpoints behind the
  // card; this read only decides whether the card and its announcement render.
  // It gates FUNDING, never spending — a wallet an admin funded is spent by
  // `web_task` whatever this says.
  PURCHASE_CREDITS_TOPUP: 'purchase-credits-topup',
  // Mirrors the backend's `location-reminders` flag (GET-18103), which gates
  // the ping endpoint and the card's location section (via the shortcut
  // status). This read decides only whether the launch announcement renders.
  LOCATION_REMINDERS: 'location-reminders',
  // Mirrors the backend's `imap-personal-inbox` flag (GET-17286): offers an
  // IMAP mailbox (iCloud, Yahoo, Fastmail…) as an additional inbox.
  IMAP_PERSONAL_INBOX: 'imap-personal-inbox',
  // Gates the tiered referrer-reward programme in-app (hotel credit per qualifying
  // referral + live progress). Mirrors the backend's `referral-tiered-program`
  // flag, which is evaluated on the referrer. Off ⇒ the referral strip/spotlight
  // show only the friend-discount + static promo copy.
  REFERRAL_TIERED_PROGRAM: 'referral-tiered-program',
  LOGIN_TRANSITION_PAGE: 'login-transition-page',
  // A/B experiment: the 'test' variant dismantles the Billing/Mail grid at the
  // top of Settings — the Mail & Calendar card moves into the integrations
  // catalog as the cell beside Messaging, and Billing moves below the
  // "you already have a message waiting" card. Hypothesis: leading with the
  // catalog (with the mail grant sitting in it as a peer) lifts integration
  // and additional-inbox connects, while a Manage Subscription button nobody
  // meets on arrival lowers same-day cancels. 'control' keeps the grid at the
  // top. Multivariate flag — read the variant, not isFeatureEnabled (which is
  // truthy for 'control' too).
  SETTINGS_MAIL_BESIDE_MESSAGING: 'settings-mail-beside-messaging',
  // A/B (experiment 548486): the add-inbox modal names when the Personal
  // Inbox add-on starts charging and needs an explicit opt-in tick before a
  // provider can be picked. Exposure is read when a billed owner opens the
  // modal. Multivariate: only 'test' treats.
  PERSONAL_INBOX_EXPLICIT_OPT_IN: 'personal-inbox-explicit-opt-in',
  // Whether the owner of a CUSTOM character may upload their own photo in
  // place of the generated headshot (and revert). Built-in characters are
  // shared rows and are never uploadable. Fails closed — the two endpoints
  // 404 when it is off, so hiding the control keeps the UI honest rather
  // than being the only thing stopping an upload.
  CUSTOM_PROFILE_PICTURE_UPLOAD: 'custom-profile-picture-upload',
  // Pages the assistant builds (GET-15593). Mirrors the backend's
  // `assistant-views`; here it only shows the Pages menu link; a page's own
  // link keeps opening whatever the flag says.
  ASSISTANT_VIEWS: 'assistant-views',
  // A/B (GET-18726): the 'test' arm of /login asks a signed-out visitor to pick
  // or build their assistant before the sign-in buttons, even on a
  // `?provider=` deep link (OAuth starts right after the pick). The pick waits
  // in localStorage and is applied on the first /onboarding mount. Multivariate
  // — read the variant, not isFeatureEnabled (truthy for 'control' too).
  PRESIGNUP_ASSISTANT_PICKER: 'presignup-assistant-picker',
  // A/B (GET-19510): the 'test' arm of the onboarding Mail & Calendar step
  // explains what the assistant does with access, shows the SOC 2 / CASA row
  // and hides the skip behind "Not now". Multivariate — read the variant; any
  // value but 'test' (or a flag that never loads) is control.
  PERMISSIONS_STEP_COPY: 'onboarding-permissions-copy',
  // A/B (GET-20066): on phone-width screens the 'test' arm of the assistant
  // picker drops the preselection and the Continue button — tapping a card
  // picks it — styles every card as a button and shows each bio in full.
  // Multivariate — read the variant; any value but 'test' is control.
  MOBILE_ASSISTANT_PICKER: 'onboarding-mobile-assistant-picker',
} as const

export type FeatureFlagName = (typeof FeatureFlags)[keyof typeof FeatureFlags]


/**
 * Parse PostHog tracking params from the URL hash *or* the query string and
 * clean them up after reading. Hash form is used for cross-domain
 * navigations from the landing site (kept intentionally client-side). Query
 * form is used for cross-browser hand-off (e.g. webview → Safari/Chrome via
 * x-safari-https:// or Android intent://, where the # delimiter is
 * unreliable for URL fragments).
 */
function getAndClearPostHogHashParams(): { sessionID?: string; distinctID?: string } {
  let sessionID: string | null = null
  let distinctID: string | null = null

  const originalHash = window.location.hash
  let cleanedHash = originalHash
  if (originalHash) {
    const hashParams = new URLSearchParams(originalHash.substring(1))
    sessionID = hashParams.get('ph_session_id')
    distinctID = hashParams.get('ph_distinct_id')
    if (sessionID || distinctID) {
      hashParams.delete('ph_session_id')
      hashParams.delete('ph_distinct_id')
      const remainder = hashParams.toString()
      cleanedHash = remainder ? '#' + remainder : ''
    }
  }

  const originalSearch = window.location.search
  let cleanedSearch = originalSearch
  if (originalSearch) {
    const searchParams = new URLSearchParams(originalSearch)
    if (!sessionID) sessionID = searchParams.get('ph_session_id')
    if (!distinctID) distinctID = searchParams.get('ph_distinct_id')
    if (searchParams.has('ph_session_id') || searchParams.has('ph_distinct_id')) {
      searchParams.delete('ph_session_id')
      searchParams.delete('ph_distinct_id')
      const remainder = searchParams.toString()
      cleanedSearch = remainder ? '?' + remainder : ''
    }
  }

  if (cleanedHash !== originalHash || cleanedSearch !== originalSearch) {
    window.history.replaceState(
      null,
      '',
      window.location.pathname + cleanedSearch + cleanedHash,
    )
  }

  return {
    ...(sessionID && { sessionID }),
    ...(distinctID && { distinctID }),
  }
}

// Public scheduling booking page. Served without a cookie-consent banner, so
// PostHog must not write cookies or localStorage when the visitor lands here.
const BOOKING_ROUTE_PREFIX = '/s/'

function isCookielessRoute(): boolean {
  return typeof window !== 'undefined'
    && window.location.pathname.startsWith(BOOKING_ROUTE_PREFIX)
}

export function initPostHog(): void {
  if (isInitialized || !POSTHOG_KEY) {
    return
  }

  const bootstrap = getAndClearPostHogHashParams()
  const hasBootstrapData = bootstrap.sessionID !== undefined || bootstrap.distinctID !== undefined
  const cookieless = isCookielessRoute()

  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST || 'https://us.i.posthog.com',
    ui_host: 'https://us.posthog.com',
    capture_pageview: false,
    capture_pageleave: false,
    // 'memory' keeps distinct_id/session_id in-memory only: no cookies, no
    // localStorage, no sessionStorage. Session recording writes to
    // localStorage, so it must be disabled alongside.
    persistence: cookieless ? 'memory' : 'localStorage+cookie',
    // Also off on any route carrying a capability token. `before_send` cannot
    // reach session replay: the rrweb meta event carries the full href inside
    // `$snapshot_data`, so a recorded `/flights/<token>` hands the token to a
    // PostHog reader however clean the event properties are. `/vault/enter-secret`
    // is the sharpest case — that page is a form people type secrets into.
    disable_session_recording: cookieless || hasCapabilityToken(window.location.href),
    autocapture: false,
    capture_exceptions: true,
    // Redact capability tokens and PII out of every URL-valued property before
    // it leaves the browser (security audit finding 17). Set here rather than
    // at the ~40 `capture` call sites because PostHog attaches `$current_url`
    // to every event it sends, including ones this module never names.
    // `before_send`, not `sanitize_properties`: the latter is deprecated as of
    // posthog-js 1.402 and does not see `$set_once`, where PostHog persists
    // `$initial_current_url` as a durable person property.
    before_send: (event) => sessionEnding ? null : sanitizeBeforeSend(event),
    ...(hasBootstrapData && { bootstrap }),
  })

  isInitialized = true
  syncDistinctIdCookie(posthog.get_distinct_id())
}

/**
 * Append the current PostHog session/distinct IDs to `url` as query
 * parameters (`ph_session_id` and `ph_distinct_id`). Used for cross-browser
 * hand-off — e.g. when a user copies a /login link out of an in-app
 * webview and pastes it into Safari, the receiving instance bootstraps
 * PostHog with the same IDs and the two sessions stitch into one Person.
 *
 * Query-param form is used (rather than hash) because Android intent://
 * URLs use `#` as their own delimiter, making fragment-based hand-off
 * fragile. The query form survives both x-safari-https:// and intent://.
 */
export function appendPostHogStitchParams(url: URL): URL {
  if (!POSTHOG_KEY) return url
  const sessionId = posthog.get_session_id?.()
  const distinctId = posthog.get_distinct_id?.()
  if (sessionId) url.searchParams.set('ph_session_id', sessionId)
  if (distinctId) url.searchParams.set('ph_distinct_id', distinctId)
  return url
}

/**
 * Build a URL hash fragment carrying the current PostHog session/distinct IDs
 * so cross-domain navigations (e.g. booking page → catchagent.ai) can stitch
 * the guest's session. Returns '' when PostHog is unavailable.
 */
export function buildPostHogHashFragment(): string {
  if (!POSTHOG_KEY) return ''
  const sessionId = posthog.get_session_id?.()
  const distinctId = posthog.get_distinct_id?.()
  if (!sessionId || !distinctId) return ''
  const params = new URLSearchParams({
    ph_session_id: sessionId,
    ph_distinct_id: distinctId,
  })
  return '#' + params.toString()
}

export function trackPageView(): void {
  if (!POSTHOG_KEY) return
  // The init-time `disable_session_recording` above only sees the entry URL.
  // These links arrive from a message, so a direct load is the dominant case —
  // but an in-app navigation into a token route would otherwise start being
  // recorded, and the rrweb meta event would carry the token.
  //
  // Deliberately one-way: this never restarts recording on the way out. A
  // session that has visited a token page already holds that URL in its
  // buffered snapshot data, so resuming would upload it anyway.
  if (hasCapabilityToken(window.location.href)) posthog.stopSessionRecording()
  posthog.capture('$pageview')
}

export function captureFrontendException(
  error: unknown,
  properties?: Record<string, unknown>,
): void {
  if (!isInitialized) return
  posthog.captureException(error, properties)
}

export function trackPageLeave(): void {
  if (!POSTHOG_KEY) return
  posthog.capture('$pageleave')
}

// Resolves once the flags loaded for the identified person have arrived.
// `identify()` registers the new distinct id synchronously but reloads flags
// behind a debounce and a network round-trip, and `onFeatureFlags` replays
// the last-received (anonymous) set synchronously in the meantime — so a
// flag read right after identify is the anonymous bucketing. Anything that
// pins an arm for a session awaits this first (GET-13932). A page that never
// identified in this load already carries the identified id in the cookie,
// so its first flag load is the right one and nothing is pending.
let pendingIdentifyReload: Promise<void> | null = null
const IDENTIFY_FLAG_RELOAD_TIMEOUT_MS = 5000

export function identifyUser(userId: string, properties?: Record<string, unknown>): void {
  if (!POSTHOG_KEY) return
  const next = envPrefixedDistinctId(userId)
  // `identify` reloads flags only when the distinct id actually changes. A
  // returning user's cookie already holds it (every OAuth sign-in passes
  // through here), so arming the wait would cost them the full timeout.
  if (getDistinctId() === next) {
    posthog.identify(next, properties)
    markIPhoneUse()
    return
  }
  const reload: Promise<void> = new Promise((resolve) => {
    // Registration replays the current set synchronously; only a callback
    // after this line is a fresh load. Fail open on the timeout: waiting
    // forever on a blocked flags request would hold the wizard's loader.
    let registered = false
    // Nor is the next callback the reload if this page's first /flags request
    // is still in flight: posthog-js queues the identified request behind it
    // and runs every handler on the anonymous response first. `hasLoadedFlags`
    // turns true only once a response has arrived in this page load.
    let anonymousResponsesPending = posthog.featureFlags.hasLoadedFlags ? 0 : 1
    const unsubscribe = posthog.onFeatureFlags(() => {
      if (!registered) return
      if (anonymousResponsesPending > 0) {
        anonymousResponsesPending -= 1
        return
      }
      unsubscribe()
      resolve()
    })
    registered = true
    setTimeout(() => {
      unsubscribe()
      resolve()
    }, IDENTIFY_FLAG_RELOAD_TIMEOUT_MS)
  })
  // Only the latest identify may clear it: an earlier one's timeout can fire
  // while a later one is still waiting on its reload.
  void reload.then(() => {
    if (pendingIdentifyReload === reload) pendingIdentifyReload = null
  })
  pendingIdentifyReload = reload
  posthog.identify(next, properties)
  markIPhoneUse()
}

export function flagsSettledForIdentifiedUser(): Promise<void> {
  return pendingIdentifyReload ?? Promise.resolve()
}

// Whether this page load has a flag response at all. False with no PostHog key,
// before the first response, and through an outage that never answers.
export function haveFeatureFlagsLoaded(): boolean {
  if (!POSTHOG_KEY) return false
  return posthog.featureFlags.hasLoadedFlags
}

// Synchronously, for a hook deciding what to render on its first pass: waiting
// a tick instead would flash the default arm at every visitor, not only at the
// just-signed-in.
export function areFlagsSettledForIdentifiedUser(): boolean {
  return pendingIdentifyReload === null
}

/**
 * A multivariate flag's value right now, recording the exposure. Straight after
 * a sign-in that is the anonymous visitor's bucketing — read an experiment arm
 * through `useExperimentVariant`, which waits that out, not directly.
 */
export function getFeatureFlagVariantSync(
  flagName: FeatureFlagName,
): string | boolean | undefined {
  if (!POSTHOG_KEY) return undefined
  return posthog.getFeatureFlag(flagName)
}

/**
 * Subscribe to flag loads. PostHog replays the current set synchronously on
 * registration when one has loaded, then calls again on every reload.
 */
export function onFeatureFlagsLoaded(callback: () => void): () => void {
  if (!POSTHOG_KEY) return () => {}
  return posthog.onFeatureFlags(callback)
}

export function setPersonProperties(properties: Record<string, unknown>): void {
  if (!POSTHOG_KEY) return
  posthog.setPersonProperties(properties)
}

// The current distinct id, for server-side captures that want to merge onto
// this browser session ($anon_distinct_id). On cookieless routes (/s/*) it is
// a per-pageload memory id — still worth passing: it ties the server event to
// the same pageload's client events.
export function getDistinctId(): string | null {
  if (!POSTHOG_KEY) return null
  return posthog.get_distinct_id?.() ?? null
}

export function resetUser(): void {
  if (!POSTHOG_KEY) return
  // Ending authentication must also end recording and drain SDK-only memory,
  // including when server logout is unavailable and the document stays open.
  sessionEnding = true
  posthog.stopSessionRecording()
  posthog.reset()
  void posthog.shutdown().catch(() => { /* Analytics must not prevent sign-out. */ })
  syncDistinctIdCookie(posthog.get_distinct_id())
}

/**
 * Opt the visitor out of all PostHog capture (events + session recording) and
 * persist the choice. Used by the /stays cookie-consent banner when a guest
 * declines.
 */
export function optOutOfTracking(): void {
  if (!POSTHOG_KEY) return
  posthog.opt_out_capturing()
}

interface TrackEventOptions {
  // Fire from a document that is being torn down — a `pagehide` it will not
  // come back from. The default path queues the event and flushes it on a later
  // tick over XHR, which a closing tab never reaches, so an unload-time event
  // delivers only sometimes; and "sometimes" is indistinguishable from the hole
  // it was added to close. `sendBeacon` hands the payload to the browser, which
  // delivers it after the document is gone.
  //
  // Not the default for everything: beacons are size-capped, cannot be retried,
  // and their response cannot be read, so they are worth spending only where
  // the alternative is losing the event outright.
  onUnload?: boolean
}

export function trackEvent(
  eventName: string,
  properties?: Record<string, unknown>,
  options?: TrackEventOptions,
): void {
  if (!POSTHOG_KEY) return
  if (options?.onUnload === true) {
    posthog.capture(eventName, properties, {
      transport: 'sendBeacon',
      // Skips the batching queue. Without it the transport override applies to
      // a flush that never happens, because the document does not survive to
      // schedule one.
      send_instantly: true,
    })
    return
  }
  posthog.capture(eventName, properties)
}

/**
 * Check if a feature flag is enabled for the current user.
 * Returns a promise that resolves when feature flags are loaded.
 */
export function isFeatureFlagEnabled(flagName: FeatureFlagName): Promise<boolean> {
  if (!POSTHOG_KEY) return Promise.resolve(false)

  return new Promise((resolve) => {
    posthog.onFeatureFlags(() => {
      resolve(posthog.isFeatureEnabled(flagName) || false)
    })
  })
}

/** A multivariate flag's cached value right now, WITHOUT recording the exposure. */
export function peekFeatureFlagVariant(
  flagName: FeatureFlagName,
): string | boolean | undefined {
  if (!POSTHOG_KEY) return undefined
  return posthog.getFeatureFlag(flagName, { send_event: false })
}

/**
 * A multivariate flag's value once flags have loaded, WITHOUT recording the
 * exposure: a caller that may give up waiting records it, with
 * `getFeatureFlagVariantSync`, only for the arm it actually renders.
 */
export function getFeatureFlagVariant(
  flagName: FeatureFlagName,
): Promise<string | boolean | undefined> {
  if (!POSTHOG_KEY) return Promise.resolve(undefined)

  return new Promise((resolve) => {
    // The callback can run synchronously during registration, before
    // `unsubscribe` exists, so drop the listener on whichever side is second.
    let settled = false
    const unsubscribe = onFeatureFlagsLoaded(() => {
      if (settled) return
      settled = true
      resolve(posthog.getFeatureFlag(flagName, { send_event: false }))
      unsubscribe?.()
    })
    if (settled) unsubscribe()
  })
}

/**
 * Synchronously check if a feature flag is enabled.
 * Returns false if flags haven't loaded yet.
 */
export function isFeatureFlagEnabledSync(flagName: FeatureFlagName): boolean {
  if (!POSTHOG_KEY) return false
  return posthog.isFeatureEnabled(flagName) || false
}

// Onboarding funnel events
export const OnboardingEvents = {
  // Sign-in flow
  SIGN_IN_PAGE_VIEWED: 'sign_in_page_viewed',
  SIGN_IN_CLICKED: 'sign_in_clicked',
  // GET-18726 pre-signup assistant picker ('test' arm of
  // presignup-assistant-picker). The pick event carries `kind`
  // (builtin|custom); skipping is the "Already have an account?" link.
  PRESIGNUP_PICKER_VIEWED: 'presignup_assistant_picker_viewed',
  PRESIGNUP_PICKER_PICKED: 'presignup_assistant_picked',
  PRESIGNUP_PICKER_SKIPPED_TO_LOGIN: 'presignup_assistant_picker_skipped_to_login',
  // Acquisition-funnel step 2. cta_clicked otherwise fires ONLY on the marketing
  // site (www.catchagent.ai), so signups that enter directly through the app
  // login page — ~1/3 of external signups, the majority never touching the
  // landing at all — never produced this event and were silently dropped from
  // the ordered "User Acquisition Funnel - External users" insight. Re-emitting
  // it when sign-in is initiated in-app closes that gap. Carries surface:'app'
  // so landing-only CTA analyses can filter it out.
  CTA_CLICKED: 'cta_clicked',
  EMAIL_PROVIDER_DETECT_SUBMITTED: 'email_provider_detect_submitted',
  EMAIL_PROVIDER_DETECTED: 'email_provider_detected',
  OAUTH_CALLBACK_RECEIVED: 'oauth_callback_received',
  SIGN_IN_COMPLETED: 'sign_in_completed',
  SIGN_IN_FAILED: 'sign_in_failed',
  // Came back to the app in the same tab after leaving for the provider,
  // without the sign-in finishing or being refused. See `signInReturn.ts`.
  SIGN_IN_RETURNED_UNFINISHED: 'sign_in_returned_unfinished',
  // The "we'll help you sign in" form under a failed sign-in (GET-20068).
  // Both carry `provider` and `reason`; submitted adds `sent`. The address
  // itself goes to the support inbox and never into an event.
  SIGN_IN_HELP_SHOWN: 'sign_in_help_shown',
  SIGN_IN_HELP_SUBMITTED: 'sign_in_help_submitted',
  // Conversion event: user has the mail + calendar scope cluster granted.
  // Fired server-side for both providers, but for Microsoft users the server
  // event precedes sign_in_completed (it fires during the OAuth callback,
  // before the redirect). This frontend-side duplicate ensures the event is
  // also timestamped *after* sign_in_completed so the ordered PostHog
  // acquisition funnel (step 3 → step 4) matches correctly.
  GRANTED_MAIL_CALENDAR: 'GrantedMailCalendar',
  IN_APP_BROWSER_DETECTED: 'in_app_browser_detected',
  IN_APP_BROWSER_ESCAPE_ATTEMPTED: 'in_app_browser_escape_attempted',
  IN_APP_BROWSER_INSTRUCTION_SHOWN: 'in_app_browser_instruction_shown',
  IN_APP_BROWSER_LINK_COPIED: 'in_app_browser_link_copied',

  // Registration waitlist (signup past the daily-registration-cap remote
  // config). The admission side is captured server-side when the drain sweep
  // activates the account, so this pairs with it to measure queue wait.
  WAITLIST_JOINED_SHOWN: 'waitlist_joined_shown',
  WAITLIST_RETRY_CLICKED: 'waitlist_retry_clicked',

  // Personal-inbox add-on paywall funnel (GET-8966). The third step —
  // personal_inbox_connected — is captured SERVER-side at callback success.
  PERSONAL_INBOX_CONNECT_CLICKED: 'personal_inbox_connect_clicked',
  PERSONAL_INBOX_PAYWALL_CONTINUE: 'personal_inbox_paywall_continue',
  // personal-inbox-explicit-opt-in test arm: the owner ticked or unticked
  // "add the add-on to my subscription" in the connect modal.
  PERSONAL_INBOX_ADDON_OPT_IN_TOGGLED: 'personal_inbox_addon_opt_in_toggled',

  // Billing
  SUBSCRIPTION_CREATED: 'subscription_created',

  // Buying prepaid purchase credits. The pair is what says whether the Settings
  // card converts: `STARTED` fires when the Stripe modal opens, and the credit
  // itself lands server-side on the paid invoice — so a gap between the two is
  // abandoned checkouts, not lost money.
  PURCHASE_CREDITS_CHECKOUT_STARTED: 'purchase_credits_checkout_started',
  PURCHASE_CREDITS_CHECKOUT_CLOSED: 'purchase_credits_checkout_closed',

  // Integration setup
  INTEGRATIONS_PAGE_VIEWED: 'integrations_page_viewed',
  WELCOME_BANNER_VIEWED: 'welcome_banner_viewed',

  // Settings-page announcement banner. Keyed by announcement id, so one pair
  // of events covers every entry the registry will ever hold — and "viewed
  // but never clicked" is the signal that an entry has stopped earning its
  // slot and should be retired.
  ANNOUNCEMENT_VIEWED: 'announcement_viewed',
  ANNOUNCEMENT_CLICKED: 'announcement_clicked',
  ANNOUNCEMENT_DISMISSED: 'announcement_dismissed',

  // "Message waiting" nudge at the bottom of the Integrations page
  ASSISTANT_MESSAGE_NUDGE_VIEWED: 'assistant_message_nudge_viewed',
  ASSISTANT_MESSAGE_NUDGE_CLICKED: 'assistant_message_nudge_clicked',

  // WhatsApp flow
  WHATSAPP_CONNECT_CLICKED: 'whatsapp_connect_clicked',
  WHATSAPP_MODAL_OPENED: 'whatsapp_modal_opened',
  WHATSAPP_CODE_GENERATED: 'whatsapp_code_generated',
  WHATSAPP_CODE_COPIED: 'whatsapp_code_copied',
  WHATSAPP_LINK_CLICKED: 'whatsapp_link_clicked',
  // "Didn't work?" under Open WhatsApp: how often the deep link fails someone
  WHATSAPP_MANUAL_OPTIONS_OPENED: 'whatsapp_manual_options_opened',
  WHATSAPP_CONNECTED: 'whatsapp_connected',
  WHATSAPP_CONNECTION_FAILED: 'whatsapp_connection_failed',
  WHATSAPP_MODAL_CLOSED: 'whatsapp_modal_closed',
  WHATSAPP_DISCONNECTED: 'whatsapp_disconnected',

  // SMS flow
  SMS_CONNECT_CLICKED: 'sms_connect_clicked',
  SMS_MODAL_OPENED: 'sms_modal_opened',
  SMS_CODE_GENERATED: 'sms_code_generated',
  SMS_CODE_COPIED: 'sms_code_copied',
  SMS_CONNECTED: 'sms_connected',
  SMS_CONNECTION_FAILED: 'sms_connection_failed',
  // Kept out of SMS_CONNECTION_FAILED: the resend cooldown is an expected wait,
  // and counting it as a failure leaves that metric unable to see a real outage.
  SMS_CODE_RATE_LIMITED: 'sms_code_rate_limited',
  SMS_MODAL_CLOSED: 'sms_modal_closed',
  SMS_DISCONNECTED: 'sms_disconnected',

  // iMessage flow
  IMESSAGE_CONNECT_CLICKED: 'imessage_connect_clicked',
  IMESSAGE_MODAL_OPENED: 'imessage_modal_opened',
  IMESSAGE_CODE_GENERATED: 'imessage_code_generated',
  IMESSAGE_LINK_CLICKED: 'imessage_link_clicked',
  IMESSAGE_CONNECTED: 'imessage_connected',
  IMESSAGE_CONNECTION_FAILED: 'imessage_connection_failed',
  // Only the CODE (control) arm can hit this — it is the one that still sends,
  // so it is the only one with a generate cooldown to run into.
  IMESSAGE_CODE_RATE_LIMITED: 'imessage_code_rate_limited',
  IMESSAGE_MODAL_CLOSED: 'imessage_modal_closed',
  IMESSAGE_DISCONNECTED: 'imessage_disconnected',

  // Slack flow
  SLACK_CONNECT_CLICKED: 'slack_connect_clicked',
  SLACK_MODAL_OPENED: 'slack_modal_opened',
  SLACK_OAUTH_STARTED: 'slack_oauth_started',
  SLACK_OAUTH_CALLBACK_RECEIVED: 'slack_oauth_callback_received',
  // Popup closed (or never opened) without the OAuth flow completing —
  // distinguishes silent give-ups from explicit failures in the funnel.
  SLACK_OAUTH_ABANDONED: 'slack_oauth_abandoned',
  SLACK_CONNECTED: 'slack_connected',
  SLACK_CONNECTION_FAILED: 'slack_connection_failed',
  SLACK_MODAL_CLOSED: 'slack_modal_closed',
  SLACK_DISCONNECTED: 'slack_disconnected',

  // Granola flow
  GRANOLA_CONNECT_CLICKED: 'granola_connect_clicked',
  GRANOLA_MODAL_OPENED: 'granola_modal_opened',
  GRANOLA_OAUTH_STARTED: 'granola_oauth_started',
  GRANOLA_CONNECTED: 'granola_connected',
  GRANOLA_CONNECTION_FAILED: 'granola_connection_failed',
  GRANOLA_MODAL_CLOSED: 'granola_modal_closed',
  GRANOLA_DISCONNECTED: 'granola_disconnected',

  // Fathom flow
  FATHOM_CONNECT_CLICKED: 'fathom_connect_clicked',
  FATHOM_MODAL_OPENED: 'fathom_modal_opened',
  FATHOM_OAUTH_STARTED: 'fathom_oauth_started',
  FATHOM_CONNECTED: 'fathom_connected',
  FATHOM_CONNECTION_FAILED: 'fathom_connection_failed',
  FATHOM_MODAL_CLOSED: 'fathom_modal_closed',
  FATHOM_DISCONNECTED: 'fathom_disconnected',

  // Otter flow
  OTTER_CONNECT_CLICKED: 'otter_connect_clicked',
  OTTER_MODAL_OPENED: 'otter_modal_opened',
  OTTER_OAUTH_STARTED: 'otter_oauth_started',
  OTTER_CONNECTED: 'otter_connected',
  OTTER_CONNECTION_FAILED: 'otter_connection_failed',
  OTTER_MODAL_CLOSED: 'otter_modal_closed',
  OTTER_DISCONNECTED: 'otter_disconnected',

  // Wispr Flow flow
  WISPR_CONNECT_CLICKED: 'wispr_connect_clicked',
  WISPR_MODAL_OPENED: 'wispr_modal_opened',
  WISPR_OAUTH_STARTED: 'wispr_oauth_started',
  WISPR_CONNECTED: 'wispr_connected',
  WISPR_CONNECTION_FAILED: 'wispr_connection_failed',
  WISPR_MODAL_CLOSED: 'wispr_modal_closed',
  WISPR_DISCONNECTED: 'wispr_disconnected',

  // ClickUp flow
  CLICKUP_CONNECT_CLICKED: 'clickup_connect_clicked',
  CLICKUP_MODAL_OPENED: 'clickup_modal_opened',
  CLICKUP_OAUTH_STARTED: 'clickup_oauth_started',
  CLICKUP_CONNECTED: 'clickup_connected',
  CLICKUP_CONNECTION_FAILED: 'clickup_connection_failed',
  CLICKUP_MODAL_CLOSED: 'clickup_modal_closed',
  CLICKUP_DISCONNECTED: 'clickup_disconnected',

  // Asana flow
  ASANA_CONNECT_CLICKED: 'asana_connect_clicked',
  ASANA_MODAL_OPENED: 'asana_modal_opened',
  ASANA_OAUTH_STARTED: 'asana_oauth_started',
  ASANA_CONNECTED: 'asana_connected',
  ASANA_CONNECTION_FAILED: 'asana_connection_failed',
  ASANA_MODAL_CLOSED: 'asana_modal_closed',
  ASANA_DISCONNECTED: 'asana_disconnected',

  // Notion flow
  NOTION_CONNECT_CLICKED: 'notion_connect_clicked',
  NOTION_MODAL_OPENED: 'notion_modal_opened',
  NOTION_OAUTH_STARTED: 'notion_oauth_started',
  NOTION_CONNECTED: 'notion_connected',
  NOTION_CONNECTION_FAILED: 'notion_connection_failed',
  NOTION_MODAL_CLOSED: 'notion_modal_closed',
  NOTION_DISCONNECTED: 'notion_disconnected',

  // HubSpot flow
  HUBSPOT_CONNECT_CLICKED: 'hubspot_connect_clicked',
  HUBSPOT_MODAL_OPENED: 'hubspot_modal_opened',
  HUBSPOT_OAUTH_STARTED: 'hubspot_oauth_started',
  HUBSPOT_CONNECTED: 'hubspot_connected',
  HUBSPOT_CONNECTION_FAILED: 'hubspot_connection_failed',
  HUBSPOT_MODAL_CLOSED: 'hubspot_modal_closed',
  HUBSPOT_DISCONNECTED: 'hubspot_disconnected',
  SALESFORCE_CONNECT_CLICKED: 'salesforce_connect_clicked',
  SALESFORCE_MODAL_OPENED: 'salesforce_modal_opened',
  SALESFORCE_OAUTH_STARTED: 'salesforce_oauth_started',
  SALESFORCE_CONNECTED: 'salesforce_connected',
  SALESFORCE_CONNECTION_FAILED: 'salesforce_connection_failed',
  SALESFORCE_MODAL_CLOSED: 'salesforce_modal_closed',
  SALESFORCE_DISCONNECTED: 'salesforce_disconnected',
  HONEYBOOK_CONNECT_CLICKED: 'honeybook_connect_clicked',
  HONEYBOOK_MODAL_OPENED: 'honeybook_modal_opened',
  HONEYBOOK_OAUTH_STARTED: 'honeybook_oauth_started',
  HONEYBOOK_CONNECTED: 'honeybook_connected',
  HONEYBOOK_CONNECTION_FAILED: 'honeybook_connection_failed',
  HONEYBOOK_MODAL_CLOSED: 'honeybook_modal_closed',
  HONEYBOOK_DISCONNECTED: 'honeybook_disconnected',

  // Attio flow
  ATTIO_CONNECT_CLICKED: 'attio_connect_clicked',
  ATTIO_MODAL_OPENED: 'attio_modal_opened',
  ATTIO_OAUTH_STARTED: 'attio_oauth_started',
  ATTIO_CONNECTED: 'attio_connected',
  ATTIO_CONNECTION_FAILED: 'attio_connection_failed',
  ATTIO_MODAL_CLOSED: 'attio_modal_closed',
  ATTIO_DISCONNECTED: 'attio_disconnected',

  // Zoho flow
  ZOHO_CONNECT_CLICKED: 'zoho_connect_clicked',
  ZOHO_MODAL_OPENED: 'zoho_modal_opened',
  ZOHO_URL_SUBMITTED: 'zoho_url_submitted',
  ZOHO_CONNECTED: 'zoho_connected',
  ZOHO_CONNECTION_FAILED: 'zoho_connection_failed',
  ZOHO_MODAL_CLOSED: 'zoho_modal_closed',
  ZOHO_DISCONNECTED: 'zoho_disconnected',

  // Assistant selection
  ASSISTANT_CHANGE_CLICKED: 'assistant_change_clicked',
  ASSISTANT_MODAL_OPENED: 'assistant_modal_opened',
  ASSISTANT_SELECTED: 'assistant_selected',
  ASSISTANT_MODAL_CLOSED: 'assistant_modal_closed',

  // Custom assistant character (create/edit form + delete)
  ASSISTANT_CUSTOM_FORM_OPENED: 'assistant_custom_form_opened',
  ASSISTANT_CUSTOM_SAVED: 'assistant_custom_saved',
  ASSISTANT_CUSTOM_PROMPT_COPIED: 'assistant_custom_prompt_copied',
  ASSISTANT_CUSTOM_SUGGESTION_APPLIED: 'assistant_custom_suggestion_applied',
  ASSISTANT_CUSTOM_FORM_CANCELLED: 'assistant_custom_form_cancelled',
  ASSISTANT_CUSTOM_DELETE_CLICKED: 'assistant_custom_delete_clicked',
  ASSISTANT_CUSTOM_DELETED: 'assistant_custom_deleted',

  // Referral strip ("Who else needs Catch?")
  // VIEWED fires on mount (the strip rendered into the DOM); IN_VIEW fires once
  // the strip actually scrolls into the viewport. Since the strip moved below the
  // fold, mount no longer implies the user saw it — IN_VIEW is the true impression.
  REFERRAL_STRIP_VIEWED: 'referral_strip_viewed',
  REFERRAL_STRIP_IN_VIEW: 'referral_strip_in_view',
  REFERRAL_INVITE_SENT: 'referral_invite_sent',
  REFERRAL_SUGGESTION_SENT: 'referral_suggestion_sent',
  REFERRAL_LINK_COPIED: 'referral_link_copied',
  REFERRAL_REWARD_CLICKED: 'referral_reward_clicked',

  // Full-network referral page (/refer) — the "big list" view. PAGE_VIEWED
  // fires on mount; NETWORK_INVITE_SENT fires per one-click invite from the
  // network list (vs REFERRAL_SUGGESTION_SENT for the strip's chips).
  REFERRAL_PAGE_VIEWED: 'referral_page_viewed',
  REFERRAL_PAGE_OPENED_FROM_STRIP: 'referral_page_opened_from_strip',
  REFERRAL_NETWORK_INVITE_SENT: 'referral_network_invite_sent',
  REFERRAL_CONTACT_DISMISSED: 'referral_contact_dismissed',
  REFERRAL_SCOPE_RECOVERY_SHOWN: 'referral_scope_recovery_shown',
  REFERRAL_SCOPE_RECOVERY_RETRY_CLICKED: 'referral_scope_recovery_retry_clicked',

  // Private $300 ambassador-offer funnel (/ambassadors + entry from /refer).
  // Enrollment happens by email, so the click is the last measurable event
  // before the request leaves the app.
  AMBASSADOR_OFFER_OPENED_FROM_REFER: 'ambassador_offer_opened_from_refer',
  AMBASSADOR_OFFER_OPT_IN_CLICKED: 'ambassador_offer_opt_in_clicked',
  AMBASSADOR_OFFER_REFER_CLICKED: 'ambassador_offer_refer_clicked',

  // Referral spotlight modal (?referral_link=1 deep link from the promo page)
  REFERRAL_SPOTLIGHT_OPENED: 'referral_spotlight_opened',
  REFERRAL_SPOTLIGHT_CLOSED: 'referral_spotlight_closed',

  // Tiered-programme progress display (behind referral-tiered-program). Fires
  // when a user is shown their live reward progress, so we can measure activation
  // lift vs the flat email-only baseline. Carries `source` (strip|spotlight) and
  // the user's referral counts.
  REFERRAL_PROGRESS_SHOWN: 'referral_progress_shown',

  // Referral acquisition funnel (referred visitor → signup)
  REFERRAL_LINK_VISITED: 'referral_link_visited',
  REFERRAL_SIGNUP_CONTEXT_SHOWN: 'referral_signup_context_shown',
  REFERRAL_SIGNUP_COMPLETED: 'referral_signup_completed',

  // Billing section
  MANAGE_SUBSCRIPTION_CLICKED: 'manage_subscription_clicked',
  SUBSCRIBE_CLICKED: 'subscribe_clicked',
  PROMO_CODE_APPLIED: 'promo_code_applied',

  // vCard ("Save contact") download from a connected messaging tile
  CONTACT_CARD_DOWNLOADED: 'contact_card_downloaded',

  // Mobile full-page-redirect OAuth return (GET-8963): the connect flow used a
  // top-level redirect instead of a popup and landed back on /integrations.
  INTEGRATION_CONNECTED_VIA_REDIRECT: 'integration_connected_via_redirect',
  INTEGRATION_CONNECT_FAILED_VIA_REDIRECT: 'integration_connect_failed_via_redirect',

  // "Other" tile + request-an-integration modal
  INTEGRATION_REQUEST_OPENED: 'integration_request_opened',
  // Inbox-scan suggestions. The dismissal rate against the connect rate is the
  // read on whether the scan is proposing the right things.
  //
  // Both carry `surface` (see IntegrationSuggestionSurface) because the two
  // places these tiles appear are not comparable: onboarding shows sixteen at
  // once with no dismiss affordance at all, the integrations page shows six and
  // offers both. A blended dismissal rate would read as scan quality when it is
  // mostly the difference between those two surfaces — and onboarding's share
  // of it has been zero since GET-11379 (#16503, 2026-08-09) made that step
  // additive.
  //
  // These fire BEFORE the dismissal request is awaited, and neither surface
  // removes the tile when it fails — the signal wanted is that the user judged
  // a suggestion wrong, not that the DELETE returned 200. So this stream is the
  // looser of the two dismissal counts: `integration_suggestions.dismissed_at`
  // is one row per key, this can be several. Reconcile by de-duping on
  // (person, catalog_key). The DB is not a denominator either — only the top
  // sixteen / top six are ever rendered, so a key can be un-dismissed simply
  // for never having been shown.
  INTEGRATION_SUGGESTION_DISMISSED: 'integration_suggestion_dismissed',
  // Fired by `useSuggestionConnect` once a tap is accepted — not by the tile
  // that was tapped. It used to be the callers', on the reasoning that only
  // they know a tap happened at all; GET-13089 inverted that. `connect` refuses
  // a tap made while another popup is still open, and a caller cannot see the
  // refusal, so every such tap filed a click that no terminal event could ever
  // close. One user tapped three tiles and was counted three times for one
  // attempt. The one surface that still emits this itself is
  // `SuggestedIntegrationsCard`'s manual (non-inline) branch, which hands off
  // to a flow outside this funnel and never reaches the hook.
  INTEGRATION_SUGGESTION_CONNECT_CLICKED: 'integration_suggestion_connect_clicked',
  // The other end of CONNECT_CLICKED, and the only thing that separates "the
  // connect went nowhere" from "the user changed their mind". A failed connect
  // is usually caught and never becomes a `$exception`, so before this it left
  // no trace at all. That is how GET-11630 stayed invisible: an iOS Safari user
  // whose `window.location.assign` was silently ignored sat on a spinning tile,
  // and the only record of it was a "Skip for now" twelve seconds later.
  //
  // Carries `surface` for the same reason the two events above do, and `reason`
  // (see ConnectFailureReason in `hooks/useSuggestionConnect`) because the
  // failures take different fixes: a blocked popup is the user's browser
  // setting, a URL error is ours, a callback failure is the provider's, and a
  // no-op navigation is a browser that accepted a redirect and never made it.
  // Fired from the hook, so it counts every failed connect exactly once
  // regardless of which surface or backend produced it.
  INTEGRATION_SUGGESTION_CONNECT_FAILED: 'integration_suggestion_connect_failed',
  // The success half, and the reason it exists separately from
  // `PIPEDREAM_APP_CONNECTED`: that event is fired only by
  // `PipedreamIntegrationsCard` (the "Connect any app" browse card), so before
  // this the entire suggestion-tile channel — the one the inbox scan exists to
  // create — produced no success event on any surface or backend. Measured on
  // 2026-08-16: two real Pipedream connections from onboarding tiles (airtable,
  // expensify) against zero `pipedream_app_connected`. Anything counting
  // adoption from that event undercounts by this whole channel; count both, and
  // do not de-dupe them against each other — they are different surfaces, not
  // two names for one funnel. Two vocabularies for one act is a cost, not a
  // design: this keys on `catalog_key` and that one on `app_slug`, and they
  // should converge if the browse card is ever moved onto this hook.
  //
  // Carries `settled_by` ('callback' | 'popup_closed') and `server_verdict`
  // ('connected' | 'not_connected' | 'unknown') rather than one fused value,
  // because they are two different authorities and the interesting rows are the
  // ones where they disagree. `settled_by: 'callback'` is the provider's own
  // page reporting success; `server_verdict` is a fresh read of the shortlist,
  // which is the codebase's actual definition of connected (a connected app
  // drops out of it). A `callback` + `not_connected` row means one of the two
  // is lying and is worth chasing; `popup_closed` + `connected` is the promoted
  // flow, whose callback page posts nothing at all and which would otherwise be
  // indistinguishable from abandonment.
  INTEGRATION_SUGGESTION_CONNECTED: 'integration_suggestion_connected',
  // Neither connected nor failed: the attempt ended with nothing to show. Split
  // out rather than left as an absence because an absence is exactly what made
  // hole 2 invisible — figma was attempted twice four minutes apart and
  // docusign once on 2026-08-16, all three ending with no connection and no
  // event, which reads identically to a user who changed their mind.
  //
  // `reason` separates the three, and they take different fixes:
  //   `closed_without_connection` — the user shut the popup and a fresh read
  //     says the app is still unconnected. This one really is intent, and it is
  //     the only one of the three that should be read that way.
  //   `unverified` — settled, but the shortlist re-read failed, so we do not
  //     know. Keep it out of any abandonment denominator; it is our outage, not
  //     their choice.
  //   `left_page` — the user navigated away with a connect still in flight (on
  //     onboarding, typically hitting Continue while the popup is open). The
  //     redirect may still have completed server-side after this fired, so pair
  //     it with the server's own connection records rather than treating it as
  //     a loss.
  //
  // Exactly one of CONNECTED / CONNECT_ABANDONED / CONNECT_FAILED fires per
  // CONNECT_CLICKED — with ONE gap left, and it is the only one that cannot be
  // closed from here: a mobile redirect that really navigates. That is a
  // different animal from the ones below, because the attempt has not ended —
  // it has moved to the next document, and the callback lands on a fresh mount
  // with no memory of the click. The honest terminal record is the server's,
  // not one this page could fire, so a CONNECT_CLICKED with no terminal event
  // on `$device_type: Mobile` is unresolved, not abandoned.
  //
  // Three gaps closed on 2026-08-27, all still in the history:
  //
  //   A tap the in-flight guard discarded emitted a click and nothing else, and
  //   an attempt whose popup was left open never settled at all (GET-13089).
  //
  //   A desktop tab closed or reloaded with the popup still open. React does
  //   not run cleanup on unload, so `left_page` never covered it — which is why
  //   that reason had fired once in 21 days. Closed by a `pagehide` listener
  //   delivering through `trackEvent`'s `onUnload` (GET-13139); the
  //   `sendBeacon` half is what makes it a fix rather than a flaky one.
  //
  // Note also that these three terminal events did not exist before 2026-08-16
  // (GET-12215), while CONNECT_CLICKED goes back to 2026-08-08. Any settle-rate
  // measured across that boundary reads every earlier click as unresolved,
  // which is how a 6% desktop stranding rate first presented as 52%. Start such
  // windows at 2026-08-17.
  INTEGRATION_SUGGESTION_CONNECT_ABANDONED: 'integration_suggestion_connect_abandoned',
  INTEGRATION_REQUEST_SUBMITTED: 'integration_request_submitted',
  // The requested app turned out to be connectable already (GET-14034), so no
  // ticket was filed. Against SUBMITTED this is the read on how much of the
  // request stream was really a discovery failure.
  INTEGRATION_REQUEST_ALREADY_AVAILABLE: 'integration_request_already_available',
  INTEGRATION_REQUEST_CLOSED: 'integration_request_closed',

  // Disconnect confirmation modal (the disconnect itself fires *_DISCONNECTED)
  INTEGRATION_DISCONNECT_CANCELLED: 'integration_disconnect_cancelled',

  // Google scope-managed info modal (Meet/Docs connected through Google)
  GOOGLE_SCOPE_INFO_CLOSED: 'google_scope_info_closed',
  GOOGLE_SCOPE_PERMISSIONS_CLICKED: 'google_scope_permissions_clicked',

  // Promoted (registry-backed) integration tiles
  PROMOTED_INTEGRATION_CONNECT_CLICKED: 'promoted_integration_connect_clicked',
  PROMOTED_INTEGRATION_DISCONNECT_CLICKED: 'promoted_integration_disconnect_clicked',
  PIPEDREAM_CATALOG_OPENED: 'pipedream_catalog_opened',
  PIPEDREAM_CATALOG_SEARCHED: 'pipedream_catalog_searched',
  // `pipedream_app_connect_clicked` / `_connected` / `_connect_failed` were
  // retired here on 2026-08-16 (GET-12219). The browse card grew its own copy
  // of the connect machinery and so its own copy of the funnel; it now shares
  // `INTEGRATION_SUGGESTION_CONNECT_CLICKED` and the three terminal events
  // above, under `surface: 'integrations_catalog'`. Historical rows keep the
  // old names — the last is 2026-08-13, 10 `pipedream_app_connected` events
  // across 4 people in 90 days, and no saved insight referenced any of them.
  //
  // Disconnect stays: it is not part of the connect funnel and has no
  // counterpart in the shared family.
  PIPEDREAM_APP_DISCONNECTED: 'pipedream_app_disconnected',

  // Custom (toolsmith) integrations card
  CUSTOM_INTEGRATION_OPENED: 'custom_integration_opened',
  CUSTOM_INTEGRATION_CONNECT_CLICKED: 'custom_integration_connect_clicked',
  CUSTOM_INTEGRATION_DELETE_CLICKED: 'custom_integration_delete_clicked',
  CUSTOM_INTEGRATION_DELETED: 'custom_integration_deleted',
  CUSTOM_INTEGRATION_MODAL_CLOSED: 'custom_integration_modal_closed',
  CUSTOM_INTEGRATION_SAMPLE_PROMPT_COPIED: 'custom_integration_sample_prompt_copied',
  CUSTOM_INTEGRATION_DELETE_CANCELLED: 'custom_integration_delete_cancelled',

  // "Shared with you" integrations card
  SHARED_INTEGRATION_OPENED: 'shared_integration_opened',
  SHARED_INTEGRATION_CONNECT_CLICKED: 'shared_integration_connect_clicked',
  SHARED_INTEGRATION_MODAL_CLOSED: 'shared_integration_modal_closed',

  // Webhook ("Other Integrations") card
  WEBHOOK_CREATE_OPENED: 'webhook_create_opened',
  WEBHOOK_CREATED: 'webhook_created',
  WEBHOOK_CREATE_CLOSED: 'webhook_create_closed',
  WEBHOOK_OPENED: 'webhook_opened',
  WEBHOOK_MODAL_CLOSED: 'webhook_modal_closed',
  WEBHOOK_TOGGLED: 'webhook_toggled',
  WEBHOOK_VALUE_COPIED: 'webhook_value_copied',
  WEBHOOK_DELETE_CLICKED: 'webhook_delete_clicked',
  WEBHOOK_DELETED: 'webhook_deleted',
  WEBHOOK_REGENERATE_CLICKED: 'webhook_regenerate_clicked',
  WEBHOOK_TOKEN_REGENERATED: 'webhook_token_regenerated',
  WEBHOOK_DELETE_CANCELLED: 'webhook_delete_cancelled',
  WEBHOOK_REGENERATE_CANCELLED: 'webhook_regenerate_cancelled',

  // Zoom flow (parity with the other providers)
  ZOOM_CONNECT_CLICKED: 'zoom_connect_clicked',
  ZOOM_MODAL_OPENED: 'zoom_modal_opened',
  ZOOM_OAUTH_STARTED: 'zoom_oauth_started',
  ZOOM_CONNECTED: 'zoom_connected',
  ZOOM_CONNECTION_FAILED: 'zoom_connection_failed',
  ZOOM_MODAL_CLOSED: 'zoom_modal_closed',
  ZOOM_DISCONNECTED: 'zoom_disconnected',

  // Verification-code "Try again" / "Generate new code" buttons
  WHATSAPP_CODE_REGENERATE_CLICKED: 'whatsapp_code_regenerate_clicked',
  SMS_CODE_REGENERATE_CLICKED: 'sms_code_regenerate_clicked',
  IMESSAGE_CODE_REGENERATE_CLICKED: 'imessage_code_regenerate_clicked',
} as const

export type OnboardingEventName = (typeof OnboardingEvents)[keyof typeof OnboardingEvents]

// Where an inbox-scan suggestion tile was acted on. One table rather than a
// literal per call site: three spellings of the same value would split the
// property into groups that silently don't add up.
export const IntegrationSuggestionSurfaces = {
  // The "Connect any app" catalog modal on Settings → Integrations. Not a
  // suggestion at all — the user searched a live catalog of thousands — which
  // makes the `integration_suggestion_*` event names a mild misnomer here. It
  // is the lesser cost: the alternative was a second connect vocabulary
  // (`pipedream_app_connected`, keyed on `app_slug`) that every reader had to
  // remember to union, and forgetting was silent. Split on this value to get
  // the old numbers back.
  //
  // Distinct from INTEGRATIONS_PAGE even though the card lives on that page:
  // that value means the "More tools you might need" row, and a browse connect
  // is a different intent from accepting something we proposed.
  INTEGRATIONS_CATALOG: 'integrations_catalog',
  // The onboarding tools step: sixteen tiles at once, connect-only. There is no
  // dismiss affordance, so this surface emits CONNECT_CLICKED and the three
  // terminal events above but never DISMISSED — an onboarding dismissal in the
  // stream is from before GET-11379 (#16503, merged 2026-08-09), which replaced
  // the subtractive keep-or-dismiss grid with an additive click-to-connect one.
  // They stop dead on that date; do not read the gap as users losing interest.
  ONBOARDING: 'onboarding',
  // The "More tools you might need" row: six tiles, connect and dismiss both
  // available. A dismissal here is a judgement the surface above cannot make.
  INTEGRATIONS_PAGE: 'integrations_page',
  // A Pipedream app we feature as a permanent tile inside its category card on
  // Settings → Integrations (Microsoft Teams, Google Sheets, SharePoint,
  // Google Drive). Its own value rather than INTEGRATIONS_PAGE, which means the
  // suggestions row on that same page: nothing proposed these to this user, so
  // folding them together would make the shortlist's accept rate move whenever
  // a permanent tile is tapped.
  INTEGRATIONS_CATEGORY_TILE: 'integrations_category_tile',
} as const

export type IntegrationSuggestionSurface =
  (typeof IntegrationSuggestionSurfaces)[keyof typeof IntegrationSuggestionSurfaces]

// Onboarding wizard events (full-page wizard flow)
export const OnboardingWizardEvents = {
  WIZARD_STARTED: 'onboarding_wizard_started',
  WIZARD_COMPLETED: 'onboarding_wizard_completed',
  WIZARD_SKIPPED: 'onboarding_wizard_skipped',

  PERMISSIONS_STEP_VIEWED: 'onboarding_permissions_step_viewed',
  PERMISSIONS_GRANT_CLICKED: 'onboarding_permissions_grant_clicked',
  PERMISSIONS_GRANTED: 'onboarding_permissions_granted',
  PERMISSIONS_MEET_TOGGLED: 'onboarding_permissions_meet_toggled',
  PERMISSIONS_DOCS_TOGGLED: 'onboarding_permissions_docs_toggled',
  PERMISSIONS_INCOMPLETE: 'onboarding_permissions_incomplete',
  // The user chose to finish setup without granting mail/calendar scopes. This
  // is the forward path out of a step that was previously a dead end, so it is
  // the denominator for "declined but stayed" — pair it with the later
  // server-side onboarding_permissions_nudge_raised to see how many come back
  // and grant once the assistant is actually talking to them.
  PERMISSIONS_DEFERRED: 'onboarding_permissions_deferred',
  // The user opened "Not now" and saw the trade-off. Opened-but-not-deferred
  // is someone the trade-off talked back into granting (GET-19510).
  PERMISSIONS_DEFER_OPENED: 'onboarding_permissions_defer_opened',

  CHARACTER_STEP_VIEWED: 'onboarding_character_step_viewed',
  CHARACTER_SELECTED: 'onboarding_character_selected',
  CHARACTER_STEP_COMPLETED: 'onboarding_character_step_completed',
  // GET-18726: the wizard applied a pick made on /login before signup, so the
  // character step was skipped (or, on failure, shown pre-filled).
  PRESIGNUP_CHARACTER_APPLIED: 'onboarding_presignup_character_applied',
  PRESIGNUP_CHARACTER_APPLY_FAILED: 'onboarding_presignup_character_apply_failed',

  CHANNEL_STEP_VIEWED: 'onboarding_channel_step_viewed',
  CHANNEL_CONNECT_CLICKED: 'onboarding_channel_connect_clicked',
  CHANNEL_CONNECTED: 'onboarding_channel_connected',
  CHANNEL_STEP_SKIPPED: 'onboarding_channel_step_skipped',

  // Billing step — the "Start your free trial" paywall shown right after channel
  // connect (unless the user already has an active subscription). This step
  // previously emitted nothing, so PostHog user streams went dark at
  // CHANNEL_CONNECTED while users were actually sitting on the credit-card
  // screen — making the paywall drop-off invisible and easy to misread as a
  // channel-connect drop-off. The terminal conversion is NOT re-emitted here:
  // the card path keeps the existing top-level `subscription_created` (fired on
  // /billing/success), and the 30-min cardless auto-grant path fires the
  // server-side `business_trial_granted` event — together they split "entered
  // card" from "auto-granted cardless trial".
  // Inbox-scan tools step. TOOLS_STEP_VIEWED carries scan_complete so a warm
  // read (the scan finished during earlier steps) can be told apart from one
  // that made the user wait — the warm rate is the number that says whether
  // the pre-warm premise holds.
  //
  // It also carries `rendered`, and the two are not the same question. An empty
  // shortlist self-continues without ever putting a grid on screen, so VIEWED
  // means "the step was reached and resolved", not "the user saw it". Reading it
  // as the latter understates the step badly: over the 24h to 2026-08-11, 4 of
  // 10 views were auto-skips, so the completion rate read 40% against a true
  // 4-of-6 (GET-11823). Filter on `rendered = true` for anything about how the
  // grid performs; use the unfiltered count only to size how often the scan
  // comes back with nothing worth showing. `load_failed` separates the third
  // case — the read itself failed — from a genuinely empty shortlist.
  //
  // `rendered` is exactly `suggestion_count > 0` at every emission site today.
  // The redundancy is deliberate, not an oversight: it names the question the
  // funnel actually asks, so a later change that renders conditionally on
  // something other than the count cannot silently invalidate every saved
  // insight built on the arithmetic. Don't drop it as duplication.
  //
  // An auto-skipped VIEWED now has a matching TOOLS_STEP_COMPLETED carrying
  // `skipped: true` with both counts at zero; before, the skip path emitted
  // nothing and was indistinguishable from abandonment. A *rendered* step still
  // legitimately produces a VIEWED with no COMPLETED — that is a user who saw
  // the grid and walked away, i.e. the drop-off this funnel exists to measure,
  // and it is not something to reconcile away.
  TOOLS_STEP_VIEWED: 'onboarding_tools_step_viewed',
  TOOLS_STEP_COMPLETED: 'onboarding_tools_step_completed',

  BILLING_STEP_VIEWED: 'onboarding_billing_step_viewed',
  BILLING_START_TRIAL_CLICKED: 'onboarding_billing_start_trial_clicked',
  BILLING_CHECKOUT_MODAL_OPENED: 'onboarding_billing_checkout_modal_opened',
  BILLING_CHECKOUT_MODAL_CLOSED: 'onboarding_billing_checkout_modal_closed',
  BILLING_CHECKOUT_START_FAILED: 'onboarding_billing_checkout_start_failed',
  // Team-invite variant of the billing step (enterprise orgs): when the account
  // has a pending team invite, the step offers "join your team" instead of the
  // individual paywall — accepting puts billing on the team (no card) and
  // completes the step. SKIPPED = chose their own billing and fell through to
  // the paywall; ACCEPTED is the terminal conversion for the join path (there
  // is no subscription_created for it — no Stripe checkout happens).
  BILLING_TEAM_INVITE_VIEWED: 'onboarding_billing_team_invite_viewed',
  BILLING_TEAM_INVITE_ACCEPTED: 'onboarding_billing_team_invite_accepted',
  BILLING_TEAM_INVITE_ACCEPT_FAILED: 'onboarding_billing_team_invite_accept_failed',
  BILLING_TEAM_INVITE_SKIPPED: 'onboarding_billing_team_invite_skipped',

  COMPLETION_STEP_VIEWED: 'onboarding_completion_step_viewed',
  DEEP_LINK_CLICKED: 'onboarding_deep_link_clicked',
  GO_TO_SETTINGS_CLICKED: 'onboarding_go_to_settings_clicked',

  SCOPE_CONNECT_CLICKED: 'scope_connect_clicked',
  // Fires when a connect never reaches the provider (our backend errored, or
  // the network is down). Without it the class is only visible as the derived
  // absence of SCOPE_CONNECTED — which is exactly how GET-12671 ran 15 hours
  // unnoticed on the login page's equivalent path (GET-12886).
  SCOPE_CONNECT_FAILED: 'scope_connect_failed',
  SCOPE_CONNECTED: 'scope_connected',
} as const

export type OnboardingWizardEventName = (typeof OnboardingWizardEvents)[keyof typeof OnboardingWizardEvents]

// Public scheduling booking page events (route: /s/:linkId). Fired for
// external guests without any cookie/localStorage persistence.
export const SchedulingEvents = {
  LINK_VIEWED: 'scheduling_link_viewed',
  SLOT_SELECTED: 'scheduling_slot_selected',
  BOOKING_SUBMITTED: 'scheduling_booking_submitted',
  BOOKING_CONFIRMED: 'scheduling_booking_confirmed',
  BOOKING_FAILED: 'scheduling_booking_failed',
  CONTACT_ASSISTANT_OPENED: 'scheduling_contact_assistant_opened',
  CONTACT_ASSISTANT_SUBMITTED: 'scheduling_contact_assistant_submitted',
  POWERED_BY_CATCH_CLICKED: 'scheduling_powered_by_catch_clicked',
  // Visitor-availability overlay ("show times you're free"): a guest connects
  // their own calendar client-side and slots get free/busy markers. All carry
  // a `provider` prop (google|microsoft). The durable lead record is the
  // server-side `scheduling_visitor_availability_lead` event, not these.
  AVAILABILITY_CONNECT_CLICKED: 'scheduling_availability_connect_clicked',
  AVAILABILITY_CONNECTED: 'scheduling_availability_connected',
  AVAILABILITY_DECLINED: 'scheduling_availability_declined',
  AVAILABILITY_ERROR: 'scheduling_availability_error',
  AVAILABILITY_MARKED: 'scheduling_availability_marked',
  AVAILABILITY_DISCONNECTED: 'scheduling_availability_disconnected',
  // A stored grant was reused with no popup (a refresh, or a second link from
  // the same host) — the measure of how often persistence saves a consent.
  AVAILABILITY_RESTORED: 'scheduling_availability_restored',
  // A restore that didn't complete. Fires against RESTORED (which counts
  // attempts), and its `grant_revoked` splits a dead grant from a flaky read.
  AVAILABILITY_RESTORE_FAILED: 'scheduling_availability_restore_failed',
} as const

export type SchedulingEventName = (typeof SchedulingEvents)[keyof typeof SchedulingEvents]

// Public accommodation options page events (route: /stays/:token). Fired for
// external guests who open the shareable map link from a chat channel.
export const StayEvents = {
  OPTIONS_VIEWED: 'stay_options_viewed',
  OPTIONS_UNAVAILABLE: 'stay_options_unavailable',
  LISTING_EXPANDED: 'stay_listing_expanded',
  OPEN_IN_GOOGLE_MAPS: 'stay_open_in_google_maps',
  MAP_PIN_TAPPED: 'stay_map_pin_tapped',
  // A price in the room table or "Choose this" on the map was tapped, which
  // opens the confirmation page (/stays/:token/confirm). `source` says which
  // of the two it was; `direct` on the later events means the page was opened
  // from its URL.
  OPTION_TAPPED: 'stay_option_tapped',
  CONFIRM_VIEWED: 'stay_confirm_viewed',
  BOOKING_CONFIRMED: 'stay_booking_confirmed',
  // `dismissed_by` is `not_now` or `back_link`. Leaving with the browser's own
  // back button records nothing.
  BOOKING_CANCELLED: 'stay_booking_cancelled',
  TERMS_OPENED: 'stay_terms_opened',
  // The selection reached the assistant. Fires only after BOOKING_CONFIRMED.
  OPTION_CHOSEN: 'stay_option_chosen',
  OPTION_ALREADY_TAKEN: 'stay_option_already_taken',
} as const

export type StayEventName = (typeof StayEvents)[keyof typeof StayEvents]

// Public flight options page events (route: /flights/:token). Fired for
// external guests who open the shareable flights link from a chat channel.
export const FlightEvents = {
  OPTIONS_VIEWED: 'flight_options_viewed',
  OPTIONS_UNAVAILABLE: 'flight_options_unavailable',
  OPTION_CHOSEN: 'flight_option_chosen',
  OPTION_ALREADY_TAKEN: 'flight_option_already_taken',
} as const

export type FlightEventName = (typeof FlightEvents)[keyof typeof FlightEvents]

// Pins "this person has an iPhone" on the PostHog person, for flags that are
// only usable from one (the `location-reminders` flag, GET-18103). PostHog's
// own `$os` person property is the OS of the LATEST visit, so an iPhone owner
// who then opens the app on a Mac would drop out of such a flag, 404 the
// shortcut endpoints and silently stop their reminders; this is set-once and
// nothing ever writes false. Only for an identified person: under
// `identified_only` person profiles a property write on an anonymous visitor
// would mint a profile for every guest (the /s/* booking page is cookieless,
// so one per pageload). Called on every load, which covers the signed-in
// visits that never pass through `identifyUser`, and from `identifyUser`
// itself, which covers the load that signs in.
export function markIPhoneUse(userAgent: string = navigator.userAgent): void {
  if (!POSTHOG_KEY || !isIPhone(userAgent)) return
  if (!isIdentifiedDistinctId(getDistinctId())) return
  posthog.setPersonProperties({}, { uses_iphone: true })
}

/**
 * Referral acquisition funnel (browser side). A referred visitor lands on
 * `?ref=CATCH-XXXX` (from the invite-email CTA redirect or a shared link). We
 * pin the code as a set-once person property so the *existing* funnel
 * (sign_in_completed → onboarding_* → subscription_created) becomes segmentable
 * by "referred vs not", and fire a "website shown" event. The code is also
 * persisted by `utils/referral` for re-applying the discount at checkout; this
 * is the analytics half. Call once at bootstrap, after `initPostHog`.
 */
export function trackReferralVisitFromUrl(): void {
  if (!POSTHOG_KEY) return
  // Normalize + format-gate so the person property and event carry one
  // canonical value — without this, raw `?ref=` junk (template placeholders,
  // full URLs, mixed case) pollutes `referred_by_code` and breaks attribution.
  const code = normalizeReferralCode(new URLSearchParams(window.location.search).get('ref'))
  if (!code) return
  // Set-once: the first referrer wins; a later visit never clobbers it.
  posthog.setPersonProperties({}, { referred_by_code: code })
  posthog.capture(OnboardingEvents.REFERRAL_LINK_VISITED, {
    referral_code: code,
    surface: 'app',
  })
}

/**
 * Fire when the signup page renders the referral context banner (invite
 * acknowledgment + discount details) for a stored code — the funnel step
 * between REFERRAL_LINK_VISITED and REFERRAL_SIGNUP_COMPLETED.
 */
export function trackReferralSignupContextShown(referralCode: string): void {
  if (!POSTHOG_KEY) return
  const code = normalizeReferralCode(referralCode)
  if (!code) return
  posthog.setPersonProperties({}, { referred_by_code: code })
  posthog.capture(OnboardingEvents.REFERRAL_SIGNUP_CONTEXT_SHOWN, {
    referral_code: code,
  })
}

/**
 * Fire when a referred visitor finishes signing in, keyed off the referral code
 * persisted across the OAuth round-trip. Re-affirms the person property so the
 * attribution lands on the now-identified user.
 */
export function trackReferralSignupCompleted(referralCode: string): void {
  if (!POSTHOG_KEY) return
  const code = normalizeReferralCode(referralCode)
  if (!code) return
  posthog.setPersonProperties({}, { referred_by_code: code })
  posthog.capture(OnboardingEvents.REFERRAL_SIGNUP_COMPLETED, {
    referral_code: code,
  })
}

/**
 * Pin the *current* user's own referral code as a person property on the
 * referrer's PostHog person. This is the missing half of referral attribution:
 * a referred user carries `referred_by_code` (set on the referee), but nothing
 * recorded the referrer's identity against that code. With `referral_code` set
 * here, attribution resolves natively via a persons self-join
 * (`referee.referred_by_code` → `referrer.referral_code`) — no backend join or
 * hand-maintained code→name map. Call when the user's own referral link loads
 * (see `useReferralLink`).
 */
export function trackOwnReferralCode(referralCode: string): void {
  const code = normalizeReferralCode(referralCode)
  if (!code) return
  setPersonProperties({ referral_code: code })
}
